import { createTextId, type TextBlock, type TextBlockType } from '../model/textDocument';
import { isHeadingBlockKind, type TextBlockKind } from '../utils/textContent';

const ORDERED_LINE_RE = /^\s*\d+[.)）、]\s*/;
const UNORDERED_LINE_RE = /^\s*(?:[-*+•●○■▪▫‣⁃]|[\u2022\u25E6\u25AA\u25CF])\s*/;

export type PasteListKind = 'ordered' | 'unordered';

export interface ParsedTextPaste {
  /** 按行拆分的纯文本（已去列表 marker、无样式） */
  lines: string[];
  isPureList: boolean;
  listKind: PasteListKind | null;
}

function normalizeLineText(text: string): string {
  return text.replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
}

function stripHtmlToPlainLines(html: string): string[] {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const body = doc.body;
  body.querySelectorAll('style, script, link, meta, head').forEach((node) => node.remove());
  body.querySelectorAll('a').forEach((anchor) => {
    anchor.replaceWith(anchor.textContent ?? '');
  });
  body.querySelectorAll('*').forEach((el) => {
    el.removeAttribute('style');
    el.removeAttribute('class');
  });
  return extractLinesFromHtmlBody(body);
}

function meaningfulElementChildren(container: HTMLElement): HTMLElement[] {
  return Array.from(container.children).filter((child) => {
    if (!(child instanceof HTMLElement)) return false;
    const tag = child.tagName;
    if (tag === 'STYLE' || tag === 'SCRIPT' || tag === 'META' || tag === 'LINK') return false;
    return normalizeLineText(child.textContent ?? '').length > 0 || tag === 'UL' || tag === 'OL' || tag === 'LI';
  }) as HTMLElement[];
}

function inlineTextInElement(element: HTMLElement): string {
  let text = '';
  element.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.textContent ?? '';
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;
    const tag = el.tagName;
    if (tag === 'BR') {
      text += '\n';
      return;
    }
    if (tag === 'UL' || tag === 'OL') return;
    text += inlineTextInElement(el);
  });
  return text;
}

function detectPureListFromHtml(body: HTMLElement): { isPureList: boolean; listKind: PasteListKind | null } {
  const top = meaningfulElementChildren(body);
  if (top.length === 0) return { isPureList: false, listKind: null };

  const listElements = top.filter((el) => el.tagName === 'UL' || el.tagName === 'OL');
  const nonList = top.filter((el) => el.tagName !== 'UL' && el.tagName !== 'OL');
  const nonListHasText = nonList.some((el) => normalizeLineText(el.textContent ?? '').length > 0);
  if (nonListHasText || listElements.length === 0) return { isPureList: false, listKind: null };

  const kinds = new Set(listElements.map((el) => (el.tagName === 'OL' ? 'ordered' : 'unordered')));
  if (kinds.size !== 1) return { isPureList: false, listKind: null };
  return { isPureList: true, listKind: [...kinds][0] as PasteListKind };
}

function linesFromListHtml(body: HTMLElement): string[] {
  const lines: string[] = [];
  body.querySelectorAll('ul, ol').forEach((list) => {
    list.querySelectorAll(':scope > li').forEach((li) => {
      const text = normalizeLineText(inlineTextInElement(li as HTMLElement));
      if (text) lines.push(text);
    });
  });
  return lines;
}

const BLOCK_TAGS = new Set([
  'P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI', 'TR', 'BLOCKQUOTE', 'PRE', 'SECTION', 'ARTICLE',
]);

function linesFromMixedHtml(container: HTMLElement): string[] {
  const lines: string[] = [];

  const walk = (node: Node, depth: number): void => {
    if (node.nodeType === Node.TEXT_NODE) {
      const chunk = (node.textContent ?? '').replace(/\u00a0/g, ' ');
      if (!chunk.trim() && lines.length > 0 && lines[lines.length - 1] !== '') {
        return;
      }
      if (chunk.trim() || lines.length === 0) {
        if (lines.length === 0) lines.push(chunk.trim() ? normalizeLineText(chunk) : '');
        else {
          const last = lines[lines.length - 1] ?? '';
          lines[lines.length - 1] = normalizeLineText(last + chunk);
        }
      }
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;
    const tag = el.tagName;
    if (tag === 'BR') {
      lines.push('');
      return;
    }
    if (tag === 'UL' || tag === 'OL') {
      el.querySelectorAll(':scope > li').forEach((li) => {
        const text = normalizeLineText(inlineTextInElement(li as HTMLElement));
        lines.push(text);
      });
      return;
    }
    if (BLOCK_TAGS.has(tag) && depth > 0) {
      const text = normalizeLineText(inlineTextInElement(el));
      lines.push(text);
      return;
    }
    el.childNodes.forEach((child) => walk(child, depth + 1));
  };

  if (meaningfulElementChildren(container).length === 0) {
    const text = normalizeLineText(container.textContent ?? '');
    return text ? [text] : [];
  }

  meaningfulElementChildren(container).forEach((child) => walk(child, 0));
  return lines.filter((line, index, arr) => line.length > 0 || (index > 0 && index < arr.length - 1));
}

function extractLinesFromHtmlBody(body: HTMLElement): string[] {
  const { isPureList } = detectPureListFromHtml(body);
  if (isPureList) return linesFromListHtml(body);
  const mixed = linesFromMixedHtml(body);
  return mixed.length ? mixed : (normalizeLineText(body.textContent ?? '') ? [normalizeLineText(body.textContent ?? '')] : []);
}

function classifyPlainLine(line: string): 'empty' | 'ordered' | 'unordered' | 'text' {
  if (!line.trim()) return 'empty';
  if (ORDERED_LINE_RE.test(line)) return 'ordered';
  if (UNORDERED_LINE_RE.test(line)) return 'unordered';
  return 'text';
}

function stripPlainListMarker(line: string, kind: PasteListKind): string {
  const stripped = kind === 'ordered'
    ? line.replace(ORDERED_LINE_RE, '')
    : line.replace(UNORDERED_LINE_RE, '');
  return normalizeLineText(stripped);
}

function parsePlainTextPaste(plain: string): ParsedTextPaste {
  const rawLines = plain.replace(/\r\n/g, '\n').split('\n');
  const nonEmptyKinds = new Set<PasteListKind | 'text'>();
  for (const line of rawLines) {
    const kind = classifyPlainLine(line);
    if (kind === 'empty') continue;
    if (kind === 'text') nonEmptyKinds.add('text');
    else nonEmptyKinds.add(kind);
  }

  const isPureList = nonEmptyKinds.size === 1
    && (nonEmptyKinds.has('ordered') || nonEmptyKinds.has('unordered'));
  const listKind = isPureList
    ? (nonEmptyKinds.has('ordered') ? 'ordered' : 'unordered')
    : null;

  const lines = rawLines.map((line) => {
    const kind = classifyPlainLine(line);
    if (kind === 'empty') return '';
    if (isPureList && listKind && (kind === 'ordered' || kind === 'unordered')) {
      return stripPlainListMarker(line, listKind);
    }
    return normalizeLineText(line);
  });

  return { lines, isPureList, listKind };
}

function parseHtmlTextPaste(html: string): ParsedTextPaste | null {
  const trimmed = html.trim();
  if (!trimmed) return null;
  const lines = stripHtmlToPlainLines(trimmed);
  const doc = new DOMParser().parseFromString(trimmed, 'text/html');
  const { isPureList, listKind } = detectPureListFromHtml(doc.body);
  return { lines, isPureList, listKind };
}

export function parseClipboardForTextPaste(clipboard: DataTransfer): ParsedTextPaste {
  const html = clipboard.getData('text/html')?.trim() ?? '';
  const plain = clipboard.getData('text/plain') ?? '';

  const fromHtml = html ? parseHtmlTextPaste(html) : null;
  if (fromHtml && fromHtml.lines.some((line) => line.length > 0)) {
    return fromHtml;
  }
  return parsePlainTextPaste(plain);
}

function resolvePasteBlockType(
  targetKind: TextBlockKind,
  parsed: ParsedTextPaste,
): TextBlockType {
  if (parsed.isPureList && parsed.listKind) {
    return parsed.listKind === 'ordered' ? 'ordered-list-item' : 'unordered-list-item';
  }
  if (isHeadingBlockKind(targetKind)) return targetKind;
  if (targetKind === 'ordered-list-item' || targetKind === 'unordered-list-item') {
    return 'paragraph';
  }
  return targetKind;
}

/** 将解析结果转为无样式 TextBlock 列表（不含链接与行内格式）。 */
export function buildPasteBlocks(
  parsed: ParsedTextPaste,
  targetBlockKind: TextBlockKind,
): TextBlock[] {
  const blockType = resolvePasteBlockType(targetBlockKind, parsed);
  const contentLines = parsed.lines.length ? parsed.lines : [''];

  return contentLines.map((line) => ({
    id: createTextId('block'),
    type: blockType,
    runs: line
      ? [{ id: createTextId('run'), text: line }]
      : [],
  }));
}
